import React from 'react';
import { Calendar, Clock, Car, Wrench, CheckCircle } from 'lucide-react';
import type { BookingState } from './bookingTypes';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const SLOT_LABELS: Record<string, string> = {
  'ts-0800': '8:00 AM', 'ts-0900': '9:00 AM', 'ts-1000': '10:00 AM',
  'ts-1100': '11:00 AM', 'ts-1200': '12:00 PM', 'ts-1300': '1:00 PM',
  'ts-1400': '2:00 PM', 'ts-1500': '3:00 PM', 'ts-1600': '4:00 PM', 'ts-1700': '5:00 PM',
};
const VEHICLE_LABELS: Record<string, string> = {
  sedan: 'Sedan', coupe: 'Coupe / Hatchback', suv: 'SUV / Crossover', truck: 'Truck / Van',
};

interface BookingSummaryPanelProps {
  booking: BookingState;
  currentStep: number;
}

export default function BookingSummaryPanel({ booking, currentStep }: BookingSummaryPanelProps) {
  const formatDate = (d: string) => {
    const [y, m, day] = d.split('-').map(Number);
    return `${MONTHS[m - 1]} ${day}, ${y}`;
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-card sticky top-24">
      {/* Header */}
      <div className="booking-gradient rounded-t-xl px-5 py-4">
        <p className="text-xs font-600 text-white/70 uppercase tracking-wider mb-0.5">Booking Summary</p>
        <p className="text-white text-sm font-500">
          {currentStep === 1 ? 'Select a service to get started' :
           currentStep === 2 ? 'Choose your date and time': 'Review and confirm below'}
        </p>
      </div>

      <div className="p-5 flex flex-col gap-4">
        {/* Service */}
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${booking.service ? 'bg-secondary' : 'bg-muted'}`}>
            <Wrench size={15} className={booking.service ? 'text-primary' : 'text-muted-foreground'} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-600 text-muted-foreground uppercase tracking-wider mb-0.5">Service</p>
            {booking.service ? (
              <>
                <p className="text-sm font-700 text-foreground">{booking.service.name}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Clock size={11} />
                  {booking.service.duration >= 60
                    ? `${Math.floor(booking.service.duration / 60)}h${booking.service.duration % 60 ? ` ${booking.service.duration % 60}m` : ''}`
                    : `${booking.service.duration}m`}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Not selected</p>
            )}
          </div>
        </div>

        {/* Vehicle */}
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${booking.vehicleType ? 'bg-secondary' : 'bg-muted'}`}>
            <Car size={15} className={booking.vehicleType ? 'text-primary' : 'text-muted-foreground'} />
          </div>
          <div>
            <p className="text-[11px] font-600 text-muted-foreground uppercase tracking-wider mb-0.5">Vehicle Type</p>
            <p className="text-sm font-600 text-foreground">{VEHICLE_LABELS[booking.vehicleType] || '—'}</p>
          </div>
        </div>

        {/* Date & Time */}
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${booking.date ? 'bg-secondary' : 'bg-muted'}`}>
            <Calendar size={15} className={booking.date ? 'text-primary' : 'text-muted-foreground'} />
          </div>
          <div>
            <p className="text-[11px] font-600 text-muted-foreground uppercase tracking-wider mb-0.5">Date & Time</p>
            {booking.date ? (
              <p className="text-sm font-600 text-foreground">
                {formatDate(booking.date)}
                {booking.timeSlot && (
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    {SLOT_LABELS[booking.timeSlot]}
                  </span>
                )}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">Not selected</p>
            )}
          </div>
        </div>

        {/* Price */}
        {booking.service && (
          <>
            <hr className="border-border" />
            <div className="flex items-center justify-between">
              <span className="text-sm font-600 text-muted-foreground">Estimated Total</span>
              <span className="text-xl font-800 text-foreground tabular-nums">${booking.service.price}</span>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">
              Payment collected at time of service. No deposit required.
            </p>
          </>
        )}

        {/* Checklist */}
        <hr className="border-border" />
        <div className="flex flex-col gap-2">
          {[
            { label: 'Service selected', done: !!booking.service },
            { label: 'Date & time chosen', done: !!booking.date && !!booking.timeSlot },
            { label: 'Contact info provided', done: !!booking.customer },
          ].map((item) => (
            <div key={`checklist-${item.label}`} className="flex items-center gap-2">
              <CheckCircle size={14} className={item.done ? 'text-emerald-500' : 'text-muted-foreground/40'} />
              <span className={`text-xs font-500 ${item.done ? 'text-foreground' : 'text-muted-foreground'}`}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}